import React, { useState, useEffect } from 'react';
import { Plus, Users, TrendingUp, History, X, Check, RefreshCw, Lock, Shield, UserPlus, Trash2 } from 'lucide-react';

const API_URL = 'https://debt-tracker-api.ttukraine.workers.dev';

export default function DebtTracker() {
  const [users, setUsers] = useState([]);
  const [debts, setDebts] = useState([]);
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('debts');
  const [showAddUser, setShowAddUser] = useState(false);
  const [showAddDebt, setShowAddDebt] = useState(false);
  const [showAddAuthorized, setShowAddAuthorized] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newDebt, setNewDebt] = useState({ from_user_id: '', to_user_id: '', amount: '', description: '' });
  const [newAuthorized, setNewAuthorized] = useState({ telegram_id: '', username: '', first_name: '', is_admin: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authorized, setAuthorized] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [telegramUser, setTelegramUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Применить тему Telegram
    if (window.Telegram && window.Telegram.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.ready();
      tg.expand();
      setIsDark(tg.colorScheme === 'dark');
      
      // Настроить цвета header
      tg.setHeaderColor(tg.colorScheme === 'dark' ? '#1e293b' : '#ffffff');
      tg.setBackgroundColor(tg.colorScheme === 'dark' ? '#0f172a' : '#f1f5f9');
    }
    
    checkAuthorization();
  }, []);

  const getTelegramUser = () => {
    if (window.Telegram && window.Telegram.WebApp) {
      const user = window.Telegram.WebApp.initDataUnsafe?.user;
      if (user) return { id: user.id, username: user.username, first_name: user.first_name, last_name: user.last_name };
    }
    return { id: 123456789, username: 'test_user', first_name: 'Test', last_name: 'User' };
  };

  const checkAuthorization = async () => {
    try {
      setCheckingAuth(true);
      const user = getTelegramUser();
      setTelegramUser(user);
      const response = await fetch(`${API_URL}/api/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telegram_id: user.id, username: user.username, first_name: user.first_name, last_name: user.last_name })
      });
      const data = await response.json();
      if (response.ok && data.authorized) {
        setAuthorized(true);
        setIsAdmin(data.is_admin);
        loadUsers();
        loadDebts();
        if (data.is_admin) loadAuthorizedUsers();
      } else {
        setAuthorized(false);
        setError(data.message || 'У вас нет доступа к этому приложению');
      }
    } catch (err) {
      setError('Ошибка проверки авторизации');
    } finally {
      setCheckingAuth(false);
    }
  };

  const getHeaders = () => ({ 'Content-Type': 'application/json', 'X-Telegram-User': JSON.stringify(telegramUser) });

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/users`, { headers: getHeaders() });
      if (response.status === 403) { setAuthorized(false); setError('Доступ запрещен'); return; }
      setUsers(await response.json());
      setError('');
    } catch (err) {
      setError('Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  };

  const loadDebts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/debts`, { headers: getHeaders() });
      if (response.status === 403) { setAuthorized(false); setError('Доступ запрещен'); return; }
      setDebts(await response.json());
      setError('');
    } catch (err) {
      setError('Ошибка загрузки долгов');
    } finally {
      setLoading(false);
    }
  };

  const loadAuthorizedUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/authorized-users`, { headers: getHeaders() });
      if (response.ok) setAuthorizedUsers(await response.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addUser = async () => {
    if (!newUserName.trim()) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ name: newUserName.trim() })
      });
      if (response.ok) { setNewUserName(''); setShowAddUser(false); await loadUsers(); }
      else setError('Ошибка добавления пользователя');
    } catch (err) {
      setError('Ошибка добавления пользователя');
    } finally {
      setLoading(false);
    }
  };

  const addDebt = async () => {
    if (!newDebt.from_user_id || !newDebt.to_user_id || !newDebt.amount) return;
    if (newDebt.from_user_id === newDebt.to_user_id) { setError('Нельзя создать долг самому себе'); return; }
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/debts`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ from_user_id: parseInt(newDebt.from_user_id), to_user_id: parseInt(newDebt.to_user_id), amount: parseFloat(newDebt.amount), description: newDebt.description })
      });
      if (response.ok) {
        setNewDebt({ from_user_id: '', to_user_id: '', amount: '', description: '' });
        setShowAddDebt(false);
        await loadDebts();
        setError('');
      } else setError('Ошибка добавления долга');
    } catch (err) {
      setError('Ошибка добавления долга');
    } finally {
      setLoading(false);
    }
  };

  const addAuthorizedUser = async () => {
    if (!newAuthorized.telegram_id) { setError('Telegram ID обязателен'); return; }
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/authorized-users`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ telegram_id: parseInt(newAuthorized.telegram_id), username: newAuthorized.username, first_name: newAuthorized.first_name, is_admin: newAuthorized.is_admin })
      });
      if (response.ok) {
        setNewAuthorized({ telegram_id: '', username: '', first_name: '', is_admin: false });
        setShowAddAuthorized(false);
        await loadAuthorizedUsers();
        setError('');
      } else {
        const data = await response.json();
        setError(data.error || 'Ошибка добавления пользователя');
      }
    } catch (err) {
      setError('Ошибка добавления пользователя');
    } finally {
      setLoading(false);
    }
  };

  const removeAuthorizedUser = async (id) => {
    if (!window.confirm('Удалить доступ этому пользователю?')) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/authorized-users/${id}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) await loadAuthorizedUsers();
      else {
        const data = await response.json();
        setError(data.error || 'Ошибка удаления пользователя');
      }
    } catch (err) {
      setError('Ошибка удаления пользователя');
    } finally {
      setLoading(false);
    }
  };

  const toggleSettle = async (id, currentSettled) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/debts/${id}`, {
        method: 'PUT', headers: getHeaders(), body: JSON.stringify({ settled: !currentSettled })
      });
      if (response.ok) await loadDebts();
      else setError('Ошибка обновления долга');
    } catch (err) {
      setError('Ошибка обновления долга');
    } finally {
      setLoading(false);
    }
  };

  const deleteDebt = async (id) => {
    if (!window.confirm('Удалить эту запись?')) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/debts/${id}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) await loadDebts();
      else setError('Ошибка удаления долга');
    } catch (err) {
      setError('Ошибка удаления долга');
    } finally {
      setLoading(false);
    }
  };

  const calculateBalances = () => {
    const balances = {};
    users.forEach(user => { balances[user.id] = { name: user.name, balance: 0 }; });
    debts.filter(d => !d.settled).forEach(debt => {
      if (balances[debt.from_user_id]) balances[debt.from_user_id].balance -= debt.amount;
      if (balances[debt.to_user_id]) balances[debt.to_user_id].balance += debt.amount;
    });
    return Object.values(balances);
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <RefreshCw className="w-12 h-12 text-indigo-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600 text-lg">Проверка доступа...</p>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Доступ запрещён</h1>
          <p className="text-gray-600 mb-2">{error}</p>
          <p className="text-sm text-gray-500 mt-4">Обратитесь к администратору приложения для получения доступа.</p>
          {telegramUser && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-2">Ваши данные для доступа:</p>
              <p className="text-sm font-mono text-gray-700">ID: {telegramUser.id}</p>
              {telegramUser.username && <p className="text-sm text-gray-600">@{telegramUser.username}</p>}
            </div>
          )}
          <button onClick={checkAuthorization} className="mt-6 bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition">
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  const balances = calculateBalances();

  const bgClass = isDark ? 'bg-slate-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100';
  const cardBg = isDark ? 'bg-slate-800' : 'bg-white';
  const textPrimary = isDark ? 'text-white' : 'text-gray-800';
  const textSecondary = isDark ? 'text-gray-300' : 'text-gray-600';
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200';

  return (
    <div className={`min-h-screen ${bgClass}`}>
      <div className="max-w-4xl mx-auto p-4">
        <div className={`${cardBg} rounded-2xl shadow-lg p-6 mb-4`}>
          <div className="flex justify-between items-center">
            <div>
              <h1 className={`text-3xl font-bold ${textPrimary} mb-2`}>
                Реестр долгов
                {isAdmin && <Shield className="inline w-6 h-6 ml-2 text-amber-500" title="Администратор" />}
              </h1>
              <p className={textSecondary}>
                Управляйте долгами в вашей группе
                {telegramUser && <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'} ml-2`}>• {telegramUser.first_name}</span>}
              </p>
            </div>
            <button onClick={() => { loadUsers(); loadDebts(); if (isAdmin) loadAuthorizedUsers(); }} disabled={loading}
              className={`${isDark ? 'bg-slate-700 text-indigo-400' : 'bg-indigo-100 text-indigo-700'} p-3 rounded-full hover:opacity-80 transition disabled:opacity-50`}>
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {error && <div className={`mt-4 ${isDark ? 'bg-red-900 border-red-700 text-red-200' : 'bg-red-100 border-red-300 text-red-700'} border px-4 py-3 rounded-lg`}>{error}</div>}
        </div>

        <div className={`${cardBg} rounded-2xl shadow-lg mb-4 p-2 flex gap-2 overflow-x-auto`}>
          <button onClick={() => setActiveTab('debts')}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition whitespace-nowrap ${activeTab === 'debts' ? 'bg-indigo-600 text-white' : `${textSecondary} ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}`}>
            <TrendingUp className="inline w-5 h-5 mr-2" />Долги
          </button>
          <button onClick={() => setActiveTab('balances')}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition whitespace-nowrap ${activeTab === 'balances' ? 'bg-indigo-600 text-white' : `${textSecondary} ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}`}>
            <Users className="inline w-5 h-5 mr-2" />Балансы
          </button>
          <button onClick={() => setActiveTab('history')}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition whitespace-nowrap ${activeTab === 'history' ? 'bg-indigo-600 text-white' : `${textSecondary} ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}`}>
            <History className="inline w-5 h-5 mr-2" />История
          </button>
          {isAdmin && (
            <button onClick={() => setActiveTab('access')}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition whitespace-nowrap ${activeTab === 'access' ? 'bg-indigo-600 text-white' : `${textSecondary} ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}`}>
              <Shield className="inline w-5 h-5 mr-2" />Доступ
            </button>
          )}
        </div>

        <div className={`${cardBg} rounded-2xl shadow-lg p-6`}>
          {activeTab === 'debts' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-2xl font-bold ${textPrimary}`}>Активные долги</h2>
                <button onClick={() => setShowAddDebt(true)} disabled={loading}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 disabled:opacity-50">
                  <Plus className="w-5 h-5" />Добавить
                </button>
              </div>
              {users.length < 2 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">Добавьте минимум 2 участника</p>
                  <button onClick={() => setShowAddUser(true)} className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition">
                    Добавить участника
                  </button>
                </div>
              ) : debts.filter(d => !d.settled).length === 0 ? (
                <div className="text-center py-12">
                  <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Нет активных долгов</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {debts.filter(d => !d.settled).map(debt => (
                    <div key={debt.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="font-semibold text-lg text-gray-800">{debt.from_user_name} → {debt.to_user_name}</div>
                          <div className="text-2xl font-bold text-indigo-600 mt-1">{debt.amount.toFixed(2)} ₴</div>
                          {debt.description && <div className="text-gray-600 text-sm mt-2">{debt.description}</div>}
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => toggleSettle(debt.id, debt.settled)} disabled={loading}
                            className="bg-green-100 text-green-700 p-2 rounded-lg hover:bg-green-200 transition disabled:opacity-50">
                            <Check className="w-5 h-5" />
                          </button>
                          <button onClick={() => deleteDebt(debt.id)} disabled={loading}
                            className="bg-red-100 text-red-700 p-2 rounded-lg hover:bg-red-200 transition disabled:opacity-50">
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 mt-2">{new Date(debt.created_at).toLocaleString('ru-RU')}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'balances' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Балансы участников</h2>
                <button onClick={() => setShowAddUser(true)} disabled={loading}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 disabled:opacity-50">
                  <Plus className="w-5 h-5" />Участник
                </button>
              </div>
              {users.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Нет участников</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {balances.map(user => (
                    <div key={user.name} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex justify-between items-center">
                        <div className="font-semibold text-lg text-gray-800">{user.name}</div>
                        <div className={`text-2xl font-bold ${user.balance > 0 ? 'text-green-600' : user.balance < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                          {user.balance > 0 ? '+' : ''}{user.balance.toFixed(2)} ₴
                        </div>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {user.balance > 0 ? 'Должны вам' : user.balance < 0 ? 'Вы должны' : 'Без долгов'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-6">История транзакций</h2>
              {debts.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">История пуста</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {debts.map(debt => (
                    <div key={debt.id} className={`border rounded-xl p-4 ${debt.settled ? 'bg-gray-50 border-gray-200' : 'border-indigo-200 bg-indigo-50'}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800">{debt.from_user_name} → {debt.to_user_name}</div>
                          <div className="font-bold text-lg text-gray-800 mt-1">{debt.amount.toFixed(2)} ₴</div>
                          {debt.description && <div className="text-gray-600 text-sm mt-1">{debt.description}</div>}
                          <div className="text-xs text-gray-400 mt-2">{new Date(debt.created_at).toLocaleString('ru-RU')}</div>
                        </div>
                        <div>
                          {debt.settled ? (
                            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">Погашено</span>
                          ) : (
                            <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">Активно</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'access' && isAdmin && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Управление доступом</h2>
                <button onClick={() => setShowAddAuthorized(true)} disabled={loading}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 disabled:opacity-50">
                  <UserPlus className="w-5 h-5" />Добавить
                </button>
              </div>
              <div className="space-y-3">
                {authorizedUsers.map(user => (
                  <div key={user.id} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-lg text-gray-800">
                            {user.first_name || user.username || `User ${user.telegram_id}`}
                          </span>
                          {user.is_admin === 1 && <Shield className="w-5 h-5 text-amber-500" title="Администратор" />}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          ID: {user.telegram_id}
                          {user.username && ` • @${user.username}`}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Добавлен: {new Date(user.created_at).toLocaleString('ru-RU')}
                        </div>
                      </div>
                      {user.telegram_id !== telegramUser.id && (
                        <button onClick={() => removeAuthorizedUser(user.id)} disabled={loading}
                          className="bg-red-100 text-red-700 p-2 rounded-lg hover:bg-red-200 transition disabled:opacity-50">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {showAddUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Добавить участника</h3>
              <input type="text" value={newUserName} onChange={(e) => setNewUserName(e.target.value)} placeholder="Имя участника"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-4" onKeyPress={(e) => e.key === 'Enter' && addUser()} disabled={loading} />
              <div className="flex gap-2">
                <button onClick={addUser} disabled={loading} className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50">
                  {loading ? 'Добавление...' : 'Добавить'}
                </button>
                <button onClick={() => { setShowAddUser(false); setNewUserName(''); }} disabled={loading}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition disabled:opacity-50">
                  Отмена
                </button>
              </div>
            </div>
          </div>
        )}

        {showAddDebt && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Добавить долг</h3>
              <div className="space-y-3">
                <select value={newDebt.from_user_id} onChange={(e) => setNewDebt({ ...newDebt, from_user_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2" disabled={loading}>
                  <option value="">Кто должен</option>
                  {users.map(user => <option key={user.id} value={user.id}>{user.name}</option>)}
                </select>
                <select value={newDebt.to_user_id} onChange={(e) => setNewDebt({ ...newDebt, to_user_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2" disabled={loading}>
                  <option value="">Кому должен</option>
                  {users.map(user => <option key={user.id} value={user.id}>{user.name}</option>)}
                </select>
                <input type="number" value={newDebt.amount} onChange={(e) => setNewDebt({ ...newDebt, amount: e.target.value })}
                  placeholder="Сумма (₴)" className="w-full border border-gray-300 rounded-lg px-4 py-2" step="0.01" disabled={loading} />
                <input type="text" value={newDebt.description} onChange={(e) => setNewDebt({ ...newDebt, description: e.target.value })}
                  placeholder="Описание (необязательно)" className="w-full border border-gray-300 rounded-lg px-4 py-2" disabled={loading} />
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={addDebt} disabled={loading} className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50">
                  {loading ? 'Добавление...' : 'Добавить'}
                </button>
                <button onClick={() => { setShowAddDebt(false); setNewDebt({ from_user_id: '', to_user_id: '', amount: '', description: '' }); }} disabled={loading}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition disabled:opacity-50">
                  Отмена
                </button>
              </div>
            </div>
          </div>
        )}

        {showAddAuthorized && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Добавить пользователя</h3>
              <div className="space-y-3">
                <input type="number" value={newAuthorized.telegram_id} onChange={(e) => setNewAuthorized({ ...newAuthorized, telegram_id: e.target.value })}
                  placeholder="Telegram ID (обязательно)" className="w-full border border-gray-300 rounded-lg px-4 py-2" disabled={loading} />
                <input type="text" value={newAuthorized.username} onChange={(e) => setNewAuthorized({ ...newAuthorized, username: e.target.value })}
                  placeholder="Username (необязательно)" className="w-full border border-gray-300 rounded-lg px-4 py-2" disabled={loading} />
                <input type="text" value={newAuthorized.first_name} onChange={(e) => setNewAuthorized({ ...newAuthorized, first_name: e.target.value })}
                  placeholder="Имя (необязательно)" className="w-full border border-gray-300 rounded-lg px-4 py-2" disabled={loading} />
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={newAuthorized.is_admin} onChange={(e) => setNewAuthorized({ ...newAuthorized, is_admin: e.target.checked })}
                    className="w-4 h-4" disabled={loading} />
                  <span className="text-gray-700">Сделать администратором</span>
                </label>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={addAuthorizedUser} disabled={loading} className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50">
                  {loading ? 'Добавление...' : 'Добавить'}
                </button>
                <button onClick={() => { setShowAddAuthorized(false); setNewAuthorized({ telegram_id: '', username: '', first_name: '', is_admin: false }); }} disabled={loading}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition disabled:opacity-50">
                  Отмена
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}